import { employeeDisplayName, isPraamis } from '../lib/praamisIdentity';
import {
  BookOpenCheck,
  Brain,
  Check,
  ChevronRight,
  MessageCircle,
  Save,
  Shield,
  SlidersHorizontal,
  Trash2,
  UserRound,
  Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { setAgentActivation, listApps } from '../lib/agentopia';
import {saveEmployeeDefinition} from '../lib/ownerControls';
import AgentCharacterAvatar from './AgentCharacterAvatar';
import AgentChat from './AgentChat';
import type {
  Agent,
  AgentopiaApp,
  AgentProfile,
  CharacterProfile,
  Behavior,
  KnowledgeEntry,
  Lesson,
  Skill,
  Situation,
  Trigger,
} from '../types';

type Props = {
  workspaceId: string;
  agents: Agent[];
  profiles: CharacterProfile[];
  selectedAgentId: string;
  onSelectAgent: (id: string) => void;
  onAgentSaved: (agent: Agent) => void;
  onOpenConnections?: () => void;
};

type Tab =
  | 'Chat'
  | 'Identity'
  | 'Mission'
  | 'Autonomy'
  | 'Triggers'
  | 'Training'
  | 'Tools';

const tabs: Array<{ name: Tab; icon: typeof UserRound }> = [
  { name: 'Identity', icon: UserRound },
  { name: 'Chat', icon: MessageCircle },
  { name: 'Mission', icon: Brain },
  { name: 'Autonomy', icon: Shield },
  { name: 'Triggers', icon: SlidersHorizontal },
  { name: 'Training', icon: BookOpenCheck },
  { name: 'Tools', icon: Wrench },
];

const uid = () => crypto.randomUUID();
const text = (value: unknown) => (typeof value === 'string' ? value : '');
const array = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

function cloneAgent(agent: Agent): Agent {
  return JSON.parse(JSON.stringify(agent)) as Agent;
}

export default function Workforce({
  workspaceId,
  agents,
  profiles,
  selectedAgentId,
  onSelectAgent,
  onAgentSaved,
  onOpenConnections,
}: Props) {
  const selected = agents.find(agent => agent.id === selectedAgentId) ?? agents[0];
  const [draft, setDraft] = useState<Agent | null>(selected ? cloneAgent(selected) : null);
  const [tab, setTab] = useState<Tab>('Identity');
  const [saving, setSaving] = useState(false);
  const [activationBusy, setActivationBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [authorizedApps,setAuthorizedApps] = useState<AgentopiaApp[]>([]);
  const [error, setError] = useState('');

  useEffect(()=>{
    if(tab!=='Chat'||!workspaceId)return;
    let active=true;
    listApps(workspaceId).then(rows=>{if(active)setAuthorizedApps(rows)}).catch(()=>{if(active)setAuthorizedApps([])});
    return()=>{active=false};
  },[tab,workspaceId]);

  useEffect(() => {
    setDraft(selected ? cloneAgent(selected) : null);
    setNotice('');
    setError('');
  }, [selected?.id, selected?.revision]);

  const dirty = useMemo(() => {
    if (!selected || !draft) return false;
    return JSON.stringify(selected) !== JSON.stringify(draft);
  }, [selected, draft]);

  if (!draft || !selected) {
    return <section className="panel empty">No workforce employee is available.</section>;
  }

  const profile = draft.profile ?? {};
  const triggers = array<Trigger>(profile.triggers);
  const situations = array<Situation>(profile.situations);
  const behaviors = array<Behavior>(profile.behaviors);
  const skills = array<Skill>(profile.skills);
  const lessons = array<Lesson>(profile.lessons);
  const knowledge = array<KnowledgeEntry>(profile.knowledge);
  const tools = array<string>(profile.tools);
  const autonomy = profile.autonomy ?? {};
  const workforcePaused = agents.every(agent => agent.enabled !== true);

  function patchAgent(patch: Partial<Agent>) {
    setDraft(current => (current ? { ...current, ...patch } : current));
    setNotice('');
  }

  function patchProfile(patch: Partial<AgentProfile>) {
    setDraft(current =>
      current
        ? { ...current, profile: { ...current.profile, ...patch } }
        : current,
    );
    setNotice('');
  }

  async function save() {
    if (!draft || !dirty || saving) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const saved = await saveEmployeeDefinition(workspaceId,draft);
      setDraft(cloneAgent(saved));
      onAgentSaved(saved);
      setNotice('Profile saved.');
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Profile could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActivation() {
    if (!draft || activationBusy) return;
    setActivationBusy(true);
    setError('');
    setNotice('');
    try {
      const nextState = draft.enabled ? 'paused' : 'active';
      const saved = await setAgentActivation(
        workspaceId,
        draft.id,
        nextState,
        nextState === 'active'
          ? 'Activated by owner from Agentopia Workforce.'
          : 'Paused by owner from Agentopia Workforce.',
      );
      setDraft(cloneAgent(saved));
      onAgentSaved(saved);
      setNotice(
        nextState === 'active'
          ? 'Employee activated. Global Guardrail policy still applies.'
          : 'Employee paused. Profile and persistent responsibility state were preserved.',
      );
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Employee activation could not be changed.');
    } finally {
      setActivationBusy(false);
    }
  }

  function setArrayItem<T>(
    key: keyof AgentProfile,
    items: T[],
    index: number,
    patch: Partial<T>,
  ) {
    const next = items.map((item, itemIndex) =>
      itemIndex === index ? { ...item, ...patch } : item,
    );
    patchProfile({ [key]: next } as Partial<AgentProfile>);
  }

  function removeArrayItem<T>(key: keyof AgentProfile, items: T[], index: number) {
    patchProfile({ [key]: items.filter((_, i) => i !== index) } as Partial<AgentProfile>);
  }

  return (
    <div className="workforce-layout">
      <section className="panel workforce-roster">
        <div className="title-row">
          <div>
            <div className="eyebrow">WORKFORCE</div>
            <h2>Employees</h2>
          </div>
          <span className="count">{agents.length}</span>
        </div>
        <div className="agent-list">
          {agents.map(agent => (
            <button
              key={agent.id}
              className={'agent-row ' + (agent.id === selected.id ? 'selected' : '')}
              onClick={() => onSelectAgent(agent.id)}
            >
              <AgentCharacterAvatar name={employeeDisplayName(agent)} roleKey={agent.role_key} profile={profiles.find(p=>p.agent_id===agent.id)} size={46}/>
              <span>
                <strong>{employeeDisplayName(agent)}</strong>
                <small>{agent.title}</small>
              </span>
              <span className={agent.enabled ? 'saved-badge' : 'lock-chip'}>
                {agent.enabled ? 'Active' : 'Paused'}
              </span>
              <ChevronRight size={16} />
            </button>
          ))}
        </div>
      </section>

      <section className="panel workforce-editor">
        <div className="title-row workforce-title">
          <div>
            <div className="eyebrow">EMPLOYEE PROFILE</div>
            <div className="agent-profile-identity"><AgentCharacterAvatar name={employeeDisplayName(draft)} roleKey={draft.role_key} profile={profiles.find(p=>p.agent_id===draft.id)} size={92}/><h2>{employeeDisplayName(draft)}</h2></div>
            <p className="muted">{draft.title}</p>
          </div>
          <div className="save-controls">
            <span className={draft.enabled ? 'saved-badge' : 'lock-chip'}>
              {draft.enabled ? 'Active' : 'Paused'}
            </span>
            <button
              className="outline-action"
              disabled={activationBusy}
              onClick={() => void toggleActivation()}
            >
              <Shield size={16} />
              {activationBusy ? 'Updating...' : draft.enabled ? 'Pause employee' : 'Activate employee'}
            </button>
            <span className={dirty ? 'unsaved-badge' : 'saved-badge'}>
              {dirty ? 'Unsaved changes' : 'Saved'}
            </span>
            <button className="primary-action" disabled={!dirty || saving} onClick={() => void save()}>
              <Save size={16} /> {saving ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </div>

        <div className="runtime-lock">
          <Shield size={18} />
          <div>
            <strong>{workforcePaused ? 'Workforce is paused' : 'Workforce activation is live'}</strong>
            <span>
              {workforcePaused
                ? 'Employee execution is blocked by the canonical activation policy.'
                : 'Profiles stay editable while Phase B activation controls govern employee execution.'}
            </span>
          </div>
        </div>

        {error && <div className="message error">{error}</div>}
        {notice && <div className="message success"><Check size={17} />{notice}</div>}

        <div className="subnav">
          {tabs.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.name}
                className={tab === item.name ? 'active' : ''}
                onClick={() => setTab(item.name)}
              >
                <Icon size={15} /> {item.name}
              </button>
            );
          })}
        </div>

        {tab === 'Chat' && (
          <AgentChat
            key={selected.id}
            employee={{id:selected.id,name:selected.official_name,role:selected.title,roleKey:selected.role_key,
              nickname:selected.nickname,status:selected.status,department:selected.department||undefined,
              profile:profiles.find(p=>p.agent_id===selected.id),tools:Array.isArray(selected.profile?.tools)?selected.profile.tools:[]}}
            roster={agents.map(a=>({id:a.id,name:a.official_name,role:a.title,roleKey:a.role_key,nickname:a.nickname,status:a.status,
              department:a.department||undefined,profile:profiles.find(p=>p.agent_id===a.id)}))}
            workspaceId={workspaceId} previewOnly={false} embedded apps={authorizedApps} onOpenConnections={onOpenConnections}
            onBack={()=>setTab('Identity')}
          />
        )}

        {tab === 'Identity' && (
          <div className="form-grid">
            <label>
              Official name
              <input
                value={draft.official_name}
                disabled={false}
                onChange={event => patchAgent({ official_name: event.target.value })}
              />
            </label>
            <label>
              Nickname
              <input
                value={draft.nickname ?? ''}
                disabled={false}
                onChange={event => patchAgent({ nickname: event.target.value || null })}
              />
            </label>
            <label className="wide">
              Title / role
              <input
                value={draft.title}
                onChange={event => patchAgent({ title: event.target.value })}
              />
            </label>
            <label>
              Department
              <input
                value={draft.department ?? ''}
                onChange={event => patchAgent({ department: event.target.value || null })}
              />
            </label>
            <div className="read-only-field">
              <span>Status</span>
              <strong>{draft.status}</strong>
            </div>
          </div>
        )}

        {tab === 'Mission' && (
          <div className="form-stack">
            <label>
              Objective
              <textarea
                rows={4}
                value={text(profile.objective)}
                onChange={event => patchProfile({ objective: event.target.value })}
              />
            </label>
            <label>
              Instructions
              <textarea
                rows={9}
                value={text(profile.instructions)}
                onChange={event => patchProfile({ instructions: event.target.value })}
              />
            </label>
            <label>
              Success criteria
              <textarea
                rows={5}
                value={text(profile.success)}
                onChange={event => patchProfile({ success: event.target.value })}
              />
            </label>
          </div>
        )}

        {tab === 'Autonomy' && (
          <div className="form-grid">
            <label className="wide">
              Autonomy mode
              <input
                value={text(autonomy.mode)}
                onChange={event =>
                  patchProfile({ autonomy: { ...autonomy, mode: event.target.value } })
                }
              />
            </label>
            <label>
              Maximum spend
              <input
                type="number"
                min="0"
                value={typeof autonomy.maxSpend === 'number' ? autonomy.maxSpend : 0}
                onChange={event =>
                  patchProfile({
                    autonomy: { ...autonomy, maxSpend: Number(event.target.value) || 0 },
                  })
                }
              />
            </label>
            {([
              ['freeOnly', 'Free-only providers'],
              ['delegate', 'Can delegate'],
              ['improve', 'Can learn/improve'],
              ['autoResume', 'Auto-resume after human gate'],
            ] as const).map(([key, label]) => (
              <label className="check-card" key={key}>
                <input
                  type="checkbox"
                  checked={Boolean(autonomy[key])}
                  onChange={event =>
                    patchProfile({
                      autonomy: { ...autonomy, [key]: event.target.checked },
                    })
                  }
                />
                <span>{label}</span>
              </label>
            ))}
            <label className="wide">
              Can start from — one per line
              <textarea
                rows={6}
                value={array<string>(autonomy.sources).join('\n')}
                onChange={event =>
                  patchProfile({
                    autonomy: {
                      ...autonomy,
                      sources: event.target.value
                        .split('\n')
                        .map(item => item.trim())
                        .filter(Boolean),
                    },
                  })
                }
              />
            </label>
          </div>
        )}

        {tab === 'Triggers' && (
          <div className="form-stack">
            <div className="section-heading">
              <div>
                <h3>When this employee should act</h3>
                <p className="muted">
                  Set the situations that should start this employee. Nothing will run while the workforce is paused.
                </p>
              </div>
              <button
                className="outline-action"
                onClick={() =>
                  patchProfile({
                    triggers: [
                      ...triggers,
                      { id: uid(), kind: 'Manual request', name: 'New trigger', enabled: false, config: {} },
                    ],
                  })
                }
              >
                Add trigger
              </button>
            </div>
            {triggers.map((trigger, index) => (
              <div className="edit-card" key={trigger.id || index}>
                <div className="edit-card-head">
                  <strong>{trigger.name || 'Untitled trigger'}</strong>
                  <button
                    className="icon-button danger"
                    onClick={() => removeArrayItem('triggers', triggers, index)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                <div className="form-grid">
                  <label>
                    Name
                    <input
                      value={trigger.name}
                      onChange={event =>
                        setArrayItem('triggers', triggers, index, { name: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Kind
                    <input
                      value={trigger.kind}
                      onChange={event =>
                        setArrayItem('triggers', triggers, index, { kind: event.target.value })
                      }
                    />
                  </label>
                  <label className="check-card wide">
                    <input
                      type="checkbox"
                      checked={Boolean(trigger.enabled)}
                      onChange={event =>
                        setArrayItem('triggers', triggers, index, { enabled: event.target.checked })
                      }
                    />
                    <span>Enabled</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'Training' && (
          <div className="form-stack">
            <TrainingSection
              title="Skills"
              items={skills}
              onChange={next => patchProfile({ skills: next })}
              kind="skill"
            />
            <TrainingSection
              title="Situations"
              items={situations}
              onChange={next => patchProfile({ situations: next })}
              kind="situation"
            />
            <TrainingSection
              title="Behaviors"
              items={behaviors}
              onChange={next => patchProfile({ behaviors: next })}
              kind="behavior"
            />
            <TrainingSection
              title="Lessons"
              items={lessons}
              onChange={next => patchProfile({ lessons: next })}
              kind="lesson"
            />
            <TrainingSection
              title="Knowledge"
              items={knowledge}
              onChange={next => patchProfile({ knowledge: next })}
              kind="knowledge"
            />
          </div>
        )}

        {tab === 'Tools' && (
          <div className="form-stack">
            <label>
              Allowed tools — one per line
              <textarea
                rows={16}
                value={tools.join('\n')}
                onChange={event =>
                  patchProfile({
                    tools: event.target.value
                      .split('\n')
                      .map(item => item.trim())
                      .filter(Boolean),
                  })
                }
              />
            </label>
            <p className="muted">
              Choose the tools this employee is allowed to use. Connections and spending permissions are controlled separately.
            </p>
          </div>
        )}

      </section>
    </div>
  );
}

type TrainingKind = 'skill' | 'situation' | 'behavior' | 'lesson' | 'knowledge';

function TrainingSection<T extends Record<string, unknown>>({
  title,
  items,
  onChange,
  kind,
}: {
  title: string;
  items: T[];
  onChange: (items: T[]) => void;
  kind: TrainingKind;
}) {
  function add() {
    const base: Record<TrainingKind, Record<string, unknown>> = {
      skill: { id: uid(), name: 'New skill', steps: '', success: '' },
      situation: { id: uid(), name: 'New situation', when: '', behaviorId: '' },
      behavior: { id: uid(), name: 'New behavior', steps: '', approval: false, retries: 0, skillId: '', output: '' },
      lesson: { id: uid(), situation: '', expected: '' },
      knowledge: { id: uid(), title: 'New knowledge', content: '', source: 'Owner supplied' },
    };
    onChange([...items, base[kind] as T]);
  }

  function update(index: number, patch: Record<string, unknown>) {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  }

  return (
    <div className="training-block">
      <div className="section-heading">
        <h3>{title} <span className="count">{items.length}</span></h3>
        <button className="outline-action" onClick={add}>Add</button>
      </div>
      {items.map((item, index) => (
        <div className="edit-card" key={String(item.id ?? index)}>
          <div className="edit-card-head">
            <strong>{String(item.name ?? item.title ?? item.situation ?? title)}</strong>
            <button className="icon-button danger" onClick={() => onChange(items.filter((_, i) => i !== index))}>
              <Trash2 size={15} />
            </button>
          </div>
          <div className="form-grid">
            {Object.entries(item)
              .filter(([key]) => !['id', 'behaviorId', 'skillId', 'retries'].includes(key))
              .map(([key, value]) => {
                if (typeof value === 'boolean') {
                  return (
                    <label className="check-card" key={key}>
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={event => update(index, { [key]: event.target.checked })}
                      />
                      <span>{key === 'approval' ? 'Needs approval' : key}</span>
                    </label>
                  );
                }
                if (typeof value === 'number') {
                  return (
                    <label key={key}>
                      {key}
                      <input
                        type="number"
                        value={value}
                        onChange={event => update(index, { [key]: Number(event.target.value) || 0 })}
                      />
                    </label>
                  );
                }
                const stringValue = typeof value === 'string' ? value : JSON.stringify(value ?? '');
                const multiline = ['steps', 'success', 'when', 'expected', 'content', 'output'].includes(key);
                return (
                  <label className={multiline ? 'wide' : ''} key={key}>
                    {key}
                    {multiline ? (
                      <textarea rows={3} value={stringValue} onChange={event => update(index, { [key]: event.target.value })} />
                    ) : (
                      <input value={stringValue} onChange={event => update(index, { [key]: event.target.value })} />
                    )}
                  </label>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
}


